/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { ActiveState, Jsonld, SeoProps } from "@eisinc/App/Widgets/SearchEngineOptimization/SearchEngineOptimization";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import {
    getSelectedCategoryPath,
    getSelectedProductPath,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductStateByPath } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { AvailabilityMessageType, ProductModel } from "@insite/client-framework/Types/ApiModels";

/**
 * get availability url based on real time inventory
 *
 * @param productInfo
 */
const getAvailability = (productInfo: ProductInfo | undefined) => {
    if (!productInfo) {
        return "";
    }

    const availability =
        productInfo?.inventory?.inventoryAvailabilityDtos?.find(
            o => o.unitOfMeasure.toLowerCase() === (productInfo?.unitOfMeasure?.toLowerCase() || ""),
        )?.availability || undefined;

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return "http://schema.org/OutOfStock";
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return "http://schema.org/InStock";
    }

    return "http://schema.org/InStock";
};

/**
 * Activate structure based on condition
 *
 * @param SeoProps
 * @returns ActiveState
 */
const activate = ({ state, currentPage, payload }: SeoProps): ActiveState => {
    const listingPage = currentPage === "Category Details" || currentPage === "Product List";
    const detailsPage = currentPage === "Product Details";

    if (listingPage) {
        payload.products = getProductListDataView(state)?.value;
        const location = getLocation(state);
        const categoryPath =
            getSelectedCategoryPath(state) ||
            (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
        const catalogPageState = getCatalogPageStateByPath(state, categoryPath);

        payload.category = getCategoryState(
            state,
            catalogPageState.value?.categoryIdWithBrandId ?? catalogPageState.value?.categoryId,
        )?.value;
        payload.brand = catalogPageState.value?.properties?.brandName;
    }

    if (detailsPage) {
        const location = getLocation(state);
        const productPath =
            getSelectedProductPath(state) ||
            (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
        const productState = getProductStateByPath(state, productPath)?.value;
        payload.products = [productState];
    }

    return (listingPage || detailsPage) && payload?.products?.length;
};

/**
 * Build the structured data
 *
 * @param SeoProps
 * @returns Jsonld
 */
const json = ({ state, payload, url, currentPage }: SeoProps): Jsonld => {
    const products: ProductModel[] = payload?.products;

    const productInfoList = (
        currentPage === "Product Details"
            ? state.pages.productDetails.productInfosById
            : state.pages.productList.productInfosByProductId
    ) as SafeDictionary<ProductInfo>;

    const seoObject: any = {
        "@context": "https://schema.org",
        "@type": currentPage === "Product Details" ? "Product" : "ProductCollection",
    };

    if (payload.category) {
        seoObject.name = payload.category.name || payload.category.shortDescription;
    }

    if (payload.brand) {
        seoObject.brand = payload.brand;
    }

    if (currentPage !== "Product Details") {
        seoObject.mainEntity = [] as any;
        // eslint-disable-next-line array-callback-return
        products?.map((product: ProductModel) => {
            const productInfo = productInfoList && product?.id ? productInfoList[product?.id] : ({} as ProductInfo);
            const item: any = {
                "@type": "Product",
                name: product?.productTitle,
                description: product?.content?.htmlContent.replace(/(<([^>]+)>)/gi, "") || product?.productTitle,
                image: product?.mediumImagePath,
                productID: product?.productNumber,
                url: url + (product?.canonicalUrl || product?.urlSegment || ""),
                sku: product?.detail?.sku || product?.productNumber,
                mpn: product?.manufacturerItem,
                offers: {
                    "@type": "Offer",
                    priceCurrency: "USD",
                    url: url + (product?.canonicalUrl || product?.urlSegment || ""),
                    availability: getAvailability(productInfo),
                },
            };
            if (product.brand) {
                item.brand = {
                    "@type": "Brand",
                    name: product?.brand?.name,
                    url: `${url}${product?.brand?.detailPagePath}`,
                    logo: product?.brand?.logoLargeImagePath,
                };
            }
            if ((productInfo?.pricing && productInfo?.pricing?.unitListPrice > 0) || product?.unitListPrice > 0) {
                const price = productInfo?.pricing?.unitListPrice || product?.unitListPrice;
                item.offers.price = price;
            } else {
                item.offers.price = 0;
            }

            if (product?.attributeTypes?.length) {
                item.additionalProperty = product.attributeTypes.reduce((acc: any[], curr) => {
                    return (acc || []).concat(
                        curr.attributeValues?.map(attributeValue => {
                            const attributeName = curr.label || curr.name;
                            return {
                                "@type": "PropertyValue",
                                name: attributeName.includes(":") ? attributeName.split(":")[1] : attributeName,
                                value: attributeValue.valueDisplay,
                            };
                        }) as any,
                    );
                }, []);
            }

            seoObject.mainEntity.push(item);
        });
        return seoObject;
    }

    // PDP page case
    seoObject.name = products[0]?.productTitle;
    seoObject.description =
        products[0]?.content?.htmlContent?.replace(/(<([^>]+)>)/gi, "") || products[0]?.productTitle;
    seoObject.image = products[0]?.mediumImagePath;
    seoObject.productID = products[0]?.productNumber;
    seoObject.url = url + (products[0]?.canonicalUrl || products[0]?.urlSegment || "");
    seoObject.sku = products[0]?.detail?.sku || products[0]?.productNumber;
    seoObject.mpn = products[0]?.manufacturerItem;
    const productInfo = productInfoList && products[0]?.id ? productInfoList[products[0]?.id] : ({} as ProductInfo);
    seoObject.offers = {
        "@type": "Offer",
        priceCurrency: "USD",
        url: url + (products[0]?.canonicalUrl || products[0]?.urlSegment || ""),
        availability: getAvailability(productInfo),
    };
    if (products[0]?.brand) {
        seoObject.brand = {
            "@type": "Brand",
            name: products[0]?.brand?.name,
            url: `${url}${products[0]?.brand?.detailPagePath}`,
            logo: products[0]?.brand?.logoLargeImagePath,
        };
    }
    if ((productInfo?.pricing && productInfo?.pricing?.unitListPrice > 0) || products[0]?.unitListPrice > 0) {
        const price = productInfo?.pricing?.unitListPrice || products[0]?.unitListPrice;
        seoObject.offers.price = price;
    } else {
        seoObject.offers.price = 0;
    }

    if (products[0]?.attributeTypes?.length) {
        seoObject.additionalProperty = products[0].attributeTypes.reduce((acc: any[], curr) => {
            return (acc || []).concat(
                curr.attributeValues?.map(attributeValue => {
                    const attributeName = curr.label || curr.name;
                    return {
                        "@type": "PropertyValue",
                        name: attributeName.includes(":") ? attributeName.split(":")[1] : attributeName,
                        value: attributeValue.valueDisplay,
                    };
                }) as any,
            );
        }, []);
    }

    return seoObject;
};

export default { activate, json };
